import { Component } from '@theme/component';
import { onDocumentLoaded, changeMetaThemeColor, setHeaderMenuStyle } from '@theme/utilities';

/**
 * @typedef {Object} HeaderComponentRefs
 * @property {HTMLDivElement} headerDrawerContainer - The header drawer container element
 * @property {HTMLElement} headerMenu - The header menu element
 * @property {HTMLElement} headerRowTop - The header top row element
 */

/**
 * @typedef {CustomEvent<{ minimumReached: boolean }>} OverflowMinimumEvent
 */

/**
 * A custom element that manages the site header.
 *
 * @extends {Component<HeaderComponentRefs>}
 */

class HeaderComponent extends Component {
  requiredRefs = ['headerDrawerContainer', 'headerMenu', 'headerRowTop'];

  /**
   * Width of window when header drawer was hidden
   * @type {number | null}
   */
  #menuDrawerHiddenWidth = null;

  /**
   * An intersection observer for monitoring sticky header position
   * @type {IntersectionObserver | null}
   */
  #intersectionObserver = null;

  /**
   * Whether the header has been scrolled offscreen, when sticky behavior is 'scroll-up'
   * @type {boolean}
   */
  #offscreen = false;

  /**
   * The last recorded scrollTop of the document, when sticky behavior is 'scroll-up
   * @type {number}
   */
  #lastScrollTop = 0;

  /**
   * A timeout to allow for hiding animation, when sticky behavior is 'scroll-up'
   * @type {number | null}
   */
  #timeout = null;

  /**
   * RAF ID for scroll handler throttling
   * @type {number | null}
   */
  #scrollRafId = null;

  /**
   * Timeout ID for the two-line menu bracket measurement
   * @type {number | null}
   */
  #twoLineBracketRafId = null;

  /**
   * Resize observer for the two-line menu bracket
   * @type {ResizeObserver | null}
   */
  #twoLineBracketResizeObserver = null;

  /**
   * Desktop media query for the two-line menu bracket
   * @type {MediaQueryList}
   */
  #twoLineBracketMedia = window.matchMedia('(min-width: 750px)');

  /**
   * Reduced motion media query for the two-line menu bracket
   * @type {MediaQueryList}
   */
  #twoLineBracketReduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /**
   * Keeps the global `--header-height` custom property up to date,
   * which other theme components can then consume
   */
  #resizeObserver = new ResizeObserver(([entry]) => {
    if (!entry || !entry.borderBoxSize[0]) return;

    // The initial height is calculated using the .offsetHeight property, which returns an integer.
    // We round to the nearest integer to avoid unnecessaary reflows.
    const roundedHeaderHeight = Math.round(entry.borderBoxSize[0].blockSize);
    document.body.style.setProperty('--header-height', `${roundedHeaderHeight}px`);

    // Check if the menu drawer should be hidden in favor of the header menu
    if (this.#menuDrawerHiddenWidth && window.innerWidth > this.#menuDrawerHiddenWidth) {
      this.#updateMenuVisibility(false);
    }
  });

  /**
   * Observes the header while scrolling the viewport to track when its actively sticky
   * @param {Boolean} alwaysSticky - Determines if we need to observe when the header is offscreen
   */
  #observeStickyPosition = (alwaysSticky = true) => {
    if (this.#intersectionObserver) return;

    const config = {
      threshold: alwaysSticky ? 1 : 0,
    };

    this.#intersectionObserver = new IntersectionObserver(([entry]) => {
      if (!entry) return;

      const { isIntersecting } = entry;

      if (alwaysSticky) {
        this.dataset.stickyState = isIntersecting ? 'inactive' : 'active';
        if (this.dataset.themeColor) changeMetaThemeColor(this.dataset.themeColor);
      } else {
        this.#offscreen = !isIntersecting || this.dataset.stickyState === 'active';
      }
    }, config);

    this.#intersectionObserver.observe(this);
  };

  /**
   * Handles the overflow minimum event from the header menu
   * @param {OverflowMinimumEvent} event
   */
  #handleOverflowMinimum = (event) => {
    this.#updateMenuVisibility(event.detail.minimumReached);
  };

  /**
   * Updates the visibility of the menu and drawer
   * @param {boolean} hideMenu - Whether to hide the menu and show the drawer
   */
  #updateMenuVisibility(hideMenu) {
    if (hideMenu) {
      this.#menuDrawerHiddenWidth = window.innerWidth;
    } else {
      this.#menuDrawerHiddenWidth = null;
    }
    setHeaderMenuStyle();
  }

  #handleWindowScroll = () => {
    if (this.#scrollRafId !== null) return;

    this.#scrollRafId = requestAnimationFrame(() => {
      this.#scrollRafId = null;
      this.#updateScrollState();
    });
  };

  #updateScrollState = () => {
    const stickyMode = this.getAttribute('sticky');
    if (!this.#offscreen && stickyMode !== 'always') return;

    const scrollTop = document.scrollingElement?.scrollTop ?? 0;
    const headerTop = this.getBoundingClientRect().top;
    const isScrollingUp = scrollTop < this.#lastScrollTop;
    const isAtTop = headerTop >= 0;

    if (this.#timeout) {
      clearTimeout(this.#timeout);
      this.#timeout = null;
    }

    if (stickyMode === 'always') {
      if (isAtTop) {
        this.dataset.scrollDirection = 'none';
      } else if (isScrollingUp) {
        this.dataset.scrollDirection = 'up';
      } else {
        this.dataset.scrollDirection = 'down';
      }

      this.#lastScrollTop = scrollTop;
      return;
    }

    if (isScrollingUp) {
      if (isAtTop) {
        // reset sticky state when header is scrolled up to natural position
        this.#offscreen = false;
        this.dataset.stickyState = 'inactive';
        this.dataset.scrollDirection = 'none';
      } else {
        // show sticky header when scrolling up
        this.dataset.stickyState = 'active';
        this.dataset.scrollDirection = 'up';
      }
    } else if (this.dataset.stickyState === 'active') {
      this.dataset.scrollDirection = 'none';

      this.dataset.stickyState = 'idle';
    } else {
      this.dataset.scrollDirection = 'none';
      this.dataset.stickyState = 'idle';
    }

    this.#lastScrollTop = scrollTop;
  };

  #scheduleTwoLineBracketReset = () => {
    this.#scheduleTwoLineBracketUpdate();
  };

  #scheduleTwoLineBracketUpdate = () => {
    delete this.dataset.twoLineBracketTarget;
    if (this.#twoLineBracketRafId !== null) return;

    this.#twoLineBracketRafId = window.setTimeout(() => {
      this.#twoLineBracketRafId = null;
      this.#updateTwoLineBracket();
    }, 16);
  };

  #getTwoLineBracketHandle() {
    const activeParent = this.querySelector(
      '.header-menu--two-line [data-two-line-parent].two-line-menu__link--active'
    );

    return activeParent instanceof HTMLElement ? activeParent.dataset.twoLineParent || '' : '';
  }

  #getTwoLineBracketParent(handle) {
    return Array.from(this.querySelectorAll('.header-menu--two-line [data-two-line-parent]')).find(
      (parent) => parent instanceof HTMLElement && parent.dataset.twoLineParent === handle
    );
  }

  #getTwoLineBracketChildNav(handle) {
    return Array.from(this.querySelectorAll('.header__navigation-bar-row [data-two-line-child-nav]')).find(
      (childNav) => childNav instanceof HTMLElement && childNav.dataset.twoLineChildNav === handle
    );
  }

  #animateTwoLineBracketPath(path, nextD) {
    const previousD = path.getAttribute('d');

    if (!previousD || this.#twoLineBracketReduceMotion.matches) {
      path.setAttribute('d', nextD);
      return;
    }

    path.setAttribute('d', nextD);

    const animation = document.createElementNS('http://www.w3.org/2000/svg', 'animate');
    animation.setAttribute('attributeName', 'd');
    animation.setAttribute('from', previousD);
    animation.setAttribute('to', nextD);
    animation.setAttribute('dur', '220ms');
    animation.setAttribute('fill', 'freeze');
    animation.setAttribute('calcMode', 'spline');
    animation.setAttribute('keySplines', '0.2 0.8 0.2 1');

    path.append(animation);
    animation.beginElement();

    window.setTimeout(() => {
      path.setAttribute('d', nextD);
      animation.remove();
    }, 240);
  }

  #hideTwoLineBracket() {
    delete this.dataset.twoLineBracketReady;
  }

  #updateTwoLineBracket() {
    const bracket = this.querySelector('[data-two-line-bracket]');
    if (!(bracket instanceof SVGSVGElement)) return;

    this.dataset.twoLineBracketReduceMotion = this.#twoLineBracketReduceMotion.matches ? 'true' : 'false';

    if (!this.#twoLineBracketMedia.matches) {
      this.#hideTwoLineBracket();
      return;
    }

    const handle = this.#getTwoLineBracketHandle();
    const parent = handle ? this.#getTwoLineBracketParent(handle) : null;
    const childNav = handle ? this.#getTwoLineBracketChildNav(handle) : null;
    const childList = childNav?.querySelector('[data-two-line-child-list]');
    const childScroll = childNav?.querySelector('.two-line-menu__scroll');
    const sourcePath = bracket.querySelector('[data-two-line-bracket-source]');
    const framePath = bracket.querySelector('[data-two-line-bracket-frame]');

    if (
      !(parent instanceof HTMLElement) ||
      !(childNav instanceof HTMLElement) ||
      !(childList instanceof HTMLElement) ||
      !(childScroll instanceof HTMLElement) ||
      !(sourcePath instanceof SVGPathElement) ||
      !(framePath instanceof SVGPathElement) ||
      parent.dataset.twoLineHasChildren !== 'true'
    ) {
      this.#hideTwoLineBracket();
      return;
    }

    const headerRect = this.getBoundingClientRect();
    const parentRect = parent.getBoundingClientRect();
    const childNavRect = childNav.getBoundingClientRect();
    const childScrollRect = childScroll.getBoundingClientRect();
    const listWidth = childList.scrollWidth;
    const parentCenter = parentRect.left + parentRect.width / 2;
    const maxOffset = Math.max(0, childScroll.clientWidth - listWidth);
    const childOffset = Math.max(0, Math.min(parentCenter - childScrollRect.left - listWidth / 2, maxOffset));

    childNav.style.setProperty('--two-line-child-offset', `${Math.round(childOffset)}px`);

    const parentLeft = Math.round(parentRect.left - headerRect.left + 2);
    const parentRight = Math.round(parentRect.right - headerRect.left - 2);
    const underlineY = Math.round(parentRect.bottom - headerRect.top - 1);
    const childLeft = Math.round(childScrollRect.left - headerRect.left + childOffset);
    const childRight = Math.round(childLeft + listWidth);
    const railY = Math.round(childNavRect.top - headerRect.top + 12);
    const dropY = Math.round(Math.min(childNavRect.bottom - headerRect.top - 5, railY + 17));
    const shoulder = 10;
    const corner = 6;

    bracket.setAttribute('width', `${Math.round(headerRect.width)}`);
    bracket.setAttribute('height', `${Math.round(headerRect.height)}`);
    bracket.setAttribute('viewBox', `0 0 ${Math.round(headerRect.width)} ${Math.round(headerRect.height)}`);

    const sourceD = `M ${parentLeft} ${underlineY} L ${parentRight} ${underlineY}`;
    const frameD = [
      `M ${childLeft} ${dropY}`,
      `L ${childLeft} ${railY + corner}`,
      `Q ${childLeft} ${railY} ${childLeft + corner} ${railY}`,
      `L ${parentLeft - shoulder} ${railY}`,
      `Q ${parentLeft - 3} ${railY} ${parentLeft} ${underlineY + 3}`,
      `L ${parentRight} ${underlineY + 3}`,
      `Q ${parentRight + 3} ${railY} ${parentRight + shoulder} ${railY}`,
      `L ${childRight - corner} ${railY}`,
      `Q ${childRight} ${railY} ${childRight} ${railY + corner}`,
      `L ${childRight} ${dropY}`,
    ].join(' ');

    this.#animateTwoLineBracketPath(sourcePath, sourceD);
    this.#animateTwoLineBracketPath(framePath, frameD);
    this.dataset.twoLineBracketReady = 'true';
  }

  #initTwoLineBracket() {
    if (!this.querySelector('[data-two-line-bracket]')) return;

    this.#twoLineBracketResizeObserver = new ResizeObserver(this.#scheduleTwoLineBracketReset);
    this.#twoLineBracketResizeObserver.observe(this);

    const watchedElements = this.querySelectorAll('[data-two-line-parent], [data-two-line-child-list]');
    for (const element of watchedElements) {
      if (element instanceof HTMLElement) {
        this.#twoLineBracketResizeObserver.observe(element);
      }
    }

    window.addEventListener('resize', this.#scheduleTwoLineBracketReset);
    this.#twoLineBracketMedia.addEventListener('change', this.#scheduleTwoLineBracketReset);
    this.#twoLineBracketReduceMotion.addEventListener('change', this.#scheduleTwoLineBracketReset);

    this.#scheduleTwoLineBracketUpdate();
    window.setTimeout(this.#scheduleTwoLineBracketReset, 150);
    window.setTimeout(this.#scheduleTwoLineBracketReset, 600);
    window.setTimeout(this.#scheduleTwoLineBracketReset, 1400);
  }

  #teardownTwoLineBracket() {
    this.#twoLineBracketResizeObserver?.disconnect();
    this.#twoLineBracketResizeObserver = null;
    window.removeEventListener('resize', this.#scheduleTwoLineBracketReset);
    this.#twoLineBracketMedia.removeEventListener('change', this.#scheduleTwoLineBracketReset);
    this.#twoLineBracketReduceMotion.removeEventListener('change', this.#scheduleTwoLineBracketReset);

    if (this.#twoLineBracketRafId !== null) {
      window.clearTimeout(this.#twoLineBracketRafId);
      this.#twoLineBracketRafId = null;
    }
  }

  connectedCallback() {
    super.connectedCallback();
    this.#resizeObserver.observe(this);
    this.addEventListener('overflowMinimum', this.#handleOverflowMinimum);
    this.#initTwoLineBracket();

    const stickyMode = this.getAttribute('sticky');
    if (stickyMode) {
      this.#observeStickyPosition(stickyMode === 'always');

      if (stickyMode === 'scroll-up' || stickyMode === 'always') {
        document.addEventListener('scroll', this.#handleWindowScroll);
      }
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
    this.#intersectionObserver?.disconnect();
    this.#teardownTwoLineBracket();
    this.removeEventListener('overflowMinimum', this.#handleOverflowMinimum);
    document.removeEventListener('scroll', this.#handleWindowScroll);
    if (this.#scrollRafId !== null) {
      cancelAnimationFrame(this.#scrollRafId);
      this.#scrollRafId = null;
    }
    document.body.style.setProperty('--header-height', '0px');
  }
}

function initTwoLineBracketFallback(header) {
  if (!(header instanceof HTMLElement) || header.dataset.twoLineBracketFallback === 'true') return;
  if (!header.querySelector('[data-two-line-bracket]')) return;

  header.dataset.twoLineBracketFallback = 'true';

  const desktopQuery = window.matchMedia('(min-width: 750px)');
  const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  let rafId = null;

  const animatePath = (path, nextD) => {
    const previousD = path.getAttribute('d');

    if (!previousD || reducedMotionQuery.matches) {
      path.setAttribute('d', nextD);
      return;
    }

    path.setAttribute('d', nextD);

    const animation = document.createElementNS('http://www.w3.org/2000/svg', 'animate');
    animation.setAttribute('attributeName', 'd');
    animation.setAttribute('from', previousD);
    animation.setAttribute('to', nextD);
    animation.setAttribute('dur', '220ms');
    animation.setAttribute('fill', 'freeze');
    animation.setAttribute('calcMode', 'spline');
    animation.setAttribute('keySplines', '0.2 0.8 0.2 1');

    path.append(animation);
    animation.beginElement();

    window.setTimeout(() => {
      path.setAttribute('d', nextD);
      animation.remove();
    }, 240);
  };

  const hideBracket = () => {
    delete header.dataset.twoLineBracketReady;
  };

  const getActiveHandle = () => {
    const activeParent = header.querySelector(
      '.header-menu--two-line [data-two-line-parent].two-line-menu__link--active'
    );

    return activeParent instanceof HTMLElement ? activeParent.dataset.twoLineParent || '' : '';
  };

  const updateBracket = () => {
    rafId = null;

    const bracket = header.querySelector('[data-two-line-bracket]');
    if (!(bracket instanceof SVGSVGElement)) return;

    header.dataset.twoLineBracketReduceMotion = reducedMotionQuery.matches ? 'true' : 'false';

    if (!desktopQuery.matches) {
      hideBracket();
      return;
    }

    const handle = getActiveHandle();
    const parent = handle
      ? header.querySelector(`.header-menu--two-line [data-two-line-parent="${CSS.escape(handle)}"]`)
      : null;
    const childNav = handle
      ? header.querySelector(`.header__navigation-bar-row [data-two-line-child-nav="${CSS.escape(handle)}"]`)
      : null;
    const childList = childNav?.querySelector('[data-two-line-child-list]');
    const childScroll = childNav?.querySelector('.two-line-menu__scroll');
    const sourcePath = bracket.querySelector('[data-two-line-bracket-source]');
    const framePath = bracket.querySelector('[data-two-line-bracket-frame]');

    if (
      !(parent instanceof HTMLElement) ||
      !(childNav instanceof HTMLElement) ||
      !(childList instanceof HTMLElement) ||
      !(childScroll instanceof HTMLElement) ||
      !(sourcePath instanceof SVGPathElement) ||
      !(framePath instanceof SVGPathElement) ||
      parent.dataset.twoLineHasChildren !== 'true'
    ) {
      hideBracket();
      return;
    }

    const headerRect = header.getBoundingClientRect();
    const parentRect = parent.getBoundingClientRect();
    const childNavRect = childNav.getBoundingClientRect();
    const childScrollRect = childScroll.getBoundingClientRect();
    const listWidth = childList.scrollWidth;
    const parentCenter = parentRect.left + parentRect.width / 2;
    const maxOffset = Math.max(0, childScroll.clientWidth - listWidth);
    const childOffset = Math.max(0, Math.min(parentCenter - childScrollRect.left - listWidth / 2, maxOffset));

    childNav.style.setProperty('--two-line-child-offset', `${Math.round(childOffset)}px`);

    const parentLeft = Math.round(parentRect.left - headerRect.left + 2);
    const parentRight = Math.round(parentRect.right - headerRect.left - 2);
    const underlineY = Math.round(parentRect.bottom - headerRect.top - 1);
    const childLeft = Math.round(childScrollRect.left - headerRect.left + childOffset);
    const childRight = Math.round(childLeft + listWidth);
    const railY = Math.round(childNavRect.top - headerRect.top + 12);
    const dropY = Math.round(Math.min(childNavRect.bottom - headerRect.top - 5, railY + 17));
    const shoulder = 10;
    const corner = 6;

    bracket.setAttribute('width', `${Math.round(headerRect.width)}`);
    bracket.setAttribute('height', `${Math.round(headerRect.height)}`);
    bracket.setAttribute('viewBox', `0 0 ${Math.round(headerRect.width)} ${Math.round(headerRect.height)}`);

    animatePath(sourcePath, `M ${parentLeft} ${underlineY} L ${parentRight} ${underlineY}`);
    animatePath(
      framePath,
      [
        `M ${childLeft} ${dropY}`,
        `L ${childLeft} ${railY + corner}`,
        `Q ${childLeft} ${railY} ${childLeft + corner} ${railY}`,
        `L ${parentLeft - shoulder} ${railY}`,
        `Q ${parentLeft - 3} ${railY} ${parentLeft} ${underlineY + 3}`,
        `L ${parentRight} ${underlineY + 3}`,
        `Q ${parentRight + 3} ${railY} ${parentRight + shoulder} ${railY}`,
        `L ${childRight - corner} ${railY}`,
        `Q ${childRight} ${railY} ${childRight} ${railY + corner}`,
        `L ${childRight} ${dropY}`,
      ].join(' ')
    );

    header.dataset.twoLineBracketReady = 'true';
  };

  const scheduleUpdate = () => {
    delete header.dataset.twoLineBracketTarget;
    if (rafId !== null) return;

    rafId = window.setTimeout(updateBracket, 16);
  };

  window.addEventListener('resize', () => scheduleUpdate());
  desktopQuery.addEventListener('change', () => scheduleUpdate());
  reducedMotionQuery.addEventListener('change', () => scheduleUpdate());

  const resizeObserver = new ResizeObserver(() => scheduleUpdate());
  resizeObserver.observe(header);
  for (const element of header.querySelectorAll('[data-two-line-parent], [data-two-line-child-list]')) {
    if (element instanceof HTMLElement) resizeObserver.observe(element);
  }

  scheduleUpdate();
  window.setTimeout(scheduleUpdate, 150);
  window.setTimeout(scheduleUpdate, 600);
  window.setTimeout(scheduleUpdate, 1400);
}

if (window.customElements && !window.customElements.get('header-component')) {
  window.customElements.define('header-component', HeaderComponent);
}

onDocumentLoaded(() => {
  const header = document.querySelector('header-component');
  const headerGroup = document.querySelector('#header-group');

  if (!window.customElements?.get('header-component')) {
    initTwoLineBracketFallback(header);
  }

  // Note: Initial header heights are set via inline script in theme.liquid
  // This ResizeObserver handles dynamic updates after page load

  // Update header group height on resize of any child
  if (headerGroup) {
    const resizeObserver = new ResizeObserver((entries) => {
      const headerGroupHeight = entries.reduce((totalHeight, entry) => {
        if (
          entry.target !== header ||
          (header.hasAttribute('transparent') && header.parentElement?.nextElementSibling)
        ) {
          return totalHeight + (entry.borderBoxSize[0]?.blockSize ?? 0);
        }
        return totalHeight;
      }, 0);
      // The initial height is calculated using the .offsetHeight property, which returns an integer.
      // We round to the nearest integer to avoid unnecessaary reflows.
      const roundedHeaderGroupHeight = Math.round(headerGroupHeight);
      document.body.style.setProperty('--header-group-height', `${roundedHeaderGroupHeight}px`);
    });

    if (header instanceof HTMLElement) {
      resizeObserver.observe(header);
    }

    // Observe all children of the header group
    const children = headerGroup.children;
    for (let i = 0; i < children.length; i++) {
      const element = children[i];
      if (element instanceof HTMLElement) {
        resizeObserver.observe(element);
      }
    }

    // Also observe the header group itself for child changes
    const mutationObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'childList') {
          // Re-observe all children when the list changes
          const children = headerGroup.children;
          for (let i = 0; i < children.length; i++) {
            const element = children[i];
            if (element instanceof HTMLElement) {
              resizeObserver.observe(element);
            }
          }
        }
      }
    });

    mutationObserver.observe(headerGroup, { childList: true });
  }
});
